/**
 * Aviso de adesão ao boleto único (tarefa #5578) — regras puras.
 *
 * Usado nas duas pontas, de propósito:
 *   - a edge function `processar-fila-boleto-unico` decide o canal e interpreta a resposta
 *     do n8n na hora de mandar;
 *   - o painel (`views/Financeiro/PainelAdesaoBoletoUnicoView.tsx`) mostra, antes de
 *     confirmar, quantas UCs vão por WhatsApp, por e-mail e quantas não têm canal.
 * Se as duas pontas tivessem cada uma a sua regra, a tela prometeria "8 por WhatsApp" e a
 * fila mandaria 7. Mesmo precedente de `carteiraRecebiveis.ts`, lido pelo front.
 *
 * FLUXO:
 *   painel enfileira a onda → cron chama a edge function → WhatsApp: n8n → UaisyChat
 *   (template `vigor_boleto_unico_aviso`); e-mail: Resend; nenhum: falha registrada.
 *   Todo resultado, sucesso ou erro, vira linha em `boleto_unico_disparos`.
 *
 * Sem imports: roda igual no Deno e no Node (testes com `node --test`).
 */

/** Nome do template aprovado na Meta (#5574). Vai gravado em cada disparo. */
export const TEMPLATE_AVISO = 'vigor_boleto_unico_aviso';

/** Link do Portal. Domínio oficial — o botão do template usa o da Vercel, que redireciona para cá. */
export const LINK_PORTAL = 'https://hub.vigorenergy.com.br/#/portal-consumidor';

// ─────────────────────────────── canal ───────────────────────────────

/**
 * Mesmo mínimo do envio de cobrança (`TELEFONE_MIN_DIGITOS` em
 * `src/lib/validacaoEnvioCobranca.ts`). Duplicado porque este arquivo não pode importar do
 * front; o teste confere que os dois continuam iguais.
 */
export const TELEFONE_MIN_DIGITOS = 10;

/** Mesma expressão de `EMAIL_RE` em `validacaoEnvioCobranca.ts` — o teste trava a paridade. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const somenteDigitos = (v: unknown): string => String(v ?? '').replace(/\D/g, '');

export type CanalAviso = 'whatsapp' | 'email' | 'nenhum';

/** Só o que a decisão de canal lê da UC. */
export interface ContatoUcAviso {
  nome_cliente?: string | null;
  cpf_cnpj_cliente?: string | null;
  whatsapp_cliente?: string | null;
  whatsapp_cliente_2?: string | null;
  email_cliente?: string | null;
  receber_cobranca_email?: boolean | null;
}

/**
 * Números como a UaisyChat recebe: só dígitos, com 55 na frente, sem repetidos.
 *
 * Filtra por tamanho ANTES de prefixar o 55, na mesma ordem de `montarPreviaEnvio`: filtrar
 * depois descartaria DDD 55 (Rio Grande do Sul).
 */
export function telefonesAviso(uc: ContatoUcAviso): string[] {
  const numeros = [uc.whatsapp_cliente, uc.whatsapp_cliente_2]
    .map(somenteDigitos)
    .filter(d => d.length >= TELEFONE_MIN_DIGITOS)
    .map(d => (d.startsWith('55') ? d : `55${d}`));
  return Array.from(new Set(numeros));
}

/**
 * O e-mail só vale com o consentimento da UC (`receber_cobranca_email`) e um endereço
 * plausível — a mesma pergunta de `emailServeComoCanal`, pedida pela tarefa.
 */
export function emailAviso(uc: ContatoUcAviso): string | null {
  const limpo = String(uc.email_cliente ?? '').trim();
  return uc.receber_cobranca_email === true && limpo && EMAIL_RE.test(limpo) ? limpo : null;
}

export interface DecisaoCanal {
  canal: CanalAviso;
  telefones: string[];
  email: string | null;
  /** Só quando `canal = 'nenhum'`: o motivo que vai para o log e para a tela. */
  motivo: string | null;
}

/** WhatsApp primeiro; e-mail só como alternativa; sem nenhum dos dois, falha com motivo. */
export function decidirCanalAviso(uc: ContatoUcAviso): DecisaoCanal {
  const telefones = telefonesAviso(uc);
  if (telefones.length > 0) return { canal: 'whatsapp', telefones, email: null, motivo: null };

  const email = emailAviso(uc);
  if (email) return { canal: 'email', telefones: [], email, motivo: null };

  // O motivo diz o que corrigir no cadastro — "sem canal" sozinho não ajuda ninguém.
  const temDigitos = Boolean(somenteDigitos(uc.whatsapp_cliente) || somenteDigitos(uc.whatsapp_cliente_2));
  const temEmail = Boolean(String(uc.email_cliente ?? '').trim());
  const partes = [
    temDigitos ? 'WhatsApp incompleto (menos de 10 dígitos)' : 'sem WhatsApp',
    !temEmail
      ? 'sem e-mail'
      : uc.receber_cobranca_email !== true
        ? 'e-mail sem autorização para cobrança'
        : 'e-mail inválido',
  ];
  return { canal: 'nenhum', telefones: [], email: null, motivo: `Sem canal: ${partes.join(' e ')}.` };
}

// ─────────────────────────────── saudação ───────────────────────────────

const capitaliza = (palavra: string): string =>
  palavra ? palavra.charAt(0).toLocaleUpperCase('pt-BR') + palavra.slice(1).toLocaleLowerCase('pt-BR') : '';

/**
 * O que entra no `{{1}}` de "Olá, {{1}}.".
 *
 * Pessoa física: primeiro nome capitalizado ("PRISCILA CRISTINA" → "Priscila"). O cadastro
 * guarda nome em caixa alta, e "Olá, PRISCILA CRISTINA DIAS TELLES." parece grito.
 * Empresa ou documento desconhecido: nome inteiro como está — capitalizar razão social
 * estraga sigla, e primeiro nome de empresa ("AGROPECUARIA") não é um nome.
 */
export function nomeSaudacao(nome?: string | null, cpfCnpj?: string | null): string {
  const limpo = String(nome ?? '').trim().replace(/\s+/g, ' ');
  if (!limpo) return 'cliente';
  if (somenteDigitos(cpfCnpj).length !== 11) return limpo;
  return capitaliza(limpo.split(' ')[0]);
}

// ─────────────────────────────── n8n ───────────────────────────────

export interface PayloadAvisoN8n {
  uc_id: number;
  uc_codigo: string;
  /** `{{1}}` do template. */
  nome: string;
  telefones: string[];
  template: string;
}

/**
 * Payload mínimo. Sem CPF, valor, endereço ou linha digitável: o template é convite, não
 * cobrança, e só usa o nome. O resto serve para o n8n registrar o que processou.
 */
export function montarPayloadN8n(
  uc: ContatoUcAviso & { id: number; numero_uc?: string | null },
  telefones: string[],
): PayloadAvisoN8n {
  return {
    uc_id: uc.id,
    uc_codigo: String(uc.numero_uc ?? '').trim(),
    nome: nomeSaudacao(uc.nome_cliente, uc.cpf_cnpj_cliente),
    telefones,
    template: TEMPLATE_AVISO,
  };
}

export interface ResultadoEnvio {
  ok: boolean;
  /** Obrigatório quando `ok = false` — vai para `boleto_unico_disparos.erro`. */
  erro: string | null;
}

/** Corta resposta longa: o log precisa do motivo, não de uma página HTML inteira. */
const trecho = (texto: string, max = 300): string => {
  const limpo = texto.replace(/\s+/g, ' ').trim();
  return limpo.length > max ? `${limpo.slice(0, max)}…` : limpo;
};

/**
 * Lê a resposta do n8n e só confirma com `ok: true` explícito no corpo.
 *
 * O n8n devolve 200 com corpo vazio quando o fluxo quebra antes do nó "Respond to
 * Webhook" — mesmo cuidado de `src/lib/liberarAssinaturasTermo.ts`. Olhar só o status HTTP
 * (como faz `processar-fila-lembrete`) registraria como entregue um aviso que não saiu.
 */
export function interpretarRespostaN8n(status: number, corpoBruto: string): ResultadoEnvio {
  // BOM (U+FEFF) na frente do corpo — vem de proxy ou de charset do n8n — faz o JSON.parse
  // recusar um `{"ok":true}` legítimo, e um aviso que SAIU seria registrado como falha.
  // Tirar o BOM não afrouxa nada: o que decide continua sendo `ok === true`.
  const corpo = String(corpoBruto ?? '').replace(/^\uFEFF/, '');
  if (status < 200 || status >= 300) {
    return { ok: false, erro: `n8n respondeu HTTP ${status}${corpo.trim() ? `: ${trecho(corpo)}` : ''}` };
  }
  if (!corpo.trim()) {
    return {
      ok: false,
      erro: 'n8n respondeu sem confirmar o envio (corpo vazio) — o fluxo falhou antes de responder. Conferir a execução no n8n.',
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(corpo);
  } catch {
    return { ok: false, erro: `n8n respondeu algo que não é JSON: ${trecho(corpo)}` };
  }

  // O Respond pode devolver objeto ou lista de um item, dependendo da configuração do nó.
  const item = (Array.isArray(parsed) ? parsed[0] : parsed) as Record<string, unknown> | null;
  if (item && item.ok === true) return { ok: true, erro: null };

  const motivo = item && (item.erro ?? item.error ?? item.message ?? item.mensagem);
  return {
    ok: false,
    erro: motivo ? `n8n não confirmou o envio: ${trecho(String(motivo))}` : 'n8n não confirmou o envio (sem "ok": true na resposta).',
  };
}

// ─────────────────────────────── e-mail ───────────────────────────────

const escapaHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface EmailAviso {
  assunto: string;
  texto: string;
  html: string;
}

/**
 * O mesmo aviso do WhatsApp, por e-mail.
 *
 * Texto espelhado do template aprovado (#5574) — só duas adaptações que o canal obriga:
 * "manda o comprovante aqui" vira "responda este e-mail", e o botão "Acessar o Portal" vira
 * link. Qualquer mudança de conteúdo precisa começar no template, não aqui.
 */
export function montarEmailAviso(nome: string): EmailAviso {
  const paragrafos = [
    `Olá, ${nome}. Aqui é da Vigor Energy.`,
    'A partir de outubro você pode juntar as duas contas em um boleto só: o valor da Vigor e o da Equatorial no mesmo documento, num pagamento único.',
    'Tem uma condição para aderir. A conta de luz não pode estar no débito automático, no Pix automático nem agendada no banco. Se estiver, o banco paga a Equatorial, a Vigor paga também, e você acaba pagando duas vezes. Cancele no seu banco e responda este e-mail com o comprovante antes de aderir.',
    'A mudança não é automática: você precisa aceitar no Portal do Consumidor.',
    'Para entrar, use o CPF do assinante. A senha do primeiro acesso são os 6 primeiros números do seu CPF, e o site pede para trocar em seguida.',
    'Se não quiser, é só não fazer nada. Continua tudo como está hoje.',
  ];

  const texto = [...paragrafos, `Acessar o Portal: ${LINK_PORTAL}`, '', 'Vigor Energy'].join('\n\n');

  const html = [
    '<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.55;color:#1f2937;max-width:560px">',
    ...paragrafos.map(p => `<p style="margin:0 0 14px">${escapaHtml(p)}</p>`),
    `<p style="margin:22px 0"><a href="${LINK_PORTAL}" style="background:#5F6C37;color:#ffffff;text-decoration:none;padding:12px 22px;border-radius:8px;font-weight:bold;display:inline-block">Acessar o Portal</a></p>`,
    '<p style="margin:0;color:#6b7280;font-size:13px">Vigor Energy</p>',
    '</div>',
  ].join('');

  return { assunto: 'Vigor Energy — Boleto único: suas duas contas em um pagamento só', texto, html };
}

// ─────────────────────────────── ritmo e modo ───────────────────────────────

/**
 * Chave em `system_settings.boleto_unico_disparo_modo`.
 *   desligado — nada entra na fila e nada sai (padrão: o documento Fase 2 exige emissão
 *               nova e aviso do Portal no ar antes do primeiro disparo);
 *   piloto    — onda de até 10 UCs (onda 0 do documento: "5 a 10");
 *   liberado  — onda de até 100.
 * Desligar no meio de uma onda para o que ainda não saiu ("Quando parar", documento Fase 2).
 */
export type ModoDisparo = 'desligado' | 'piloto' | 'liberado';

export const LIMITE_ONDA: Record<ModoDisparo, number> = { desligado: 0, piloto: 10, liberado: 100 };

export function lerModoDisparo(valor: unknown): ModoDisparo {
  const v = String(valor ?? '').trim().toLowerCase();
  return v === 'piloto' || v === 'liberado' ? v : 'desligado';
}

/**
 * Intervalo médio entre mensagens, em segundos: 2 a 3 minutos, o mesmo ritmo da régua de
 * cobrança (`lembrete-cobranca`), que já roda com este número da UaisyChat sem queda de
 * qualidade na Meta. A fila sorteia dentro da faixa; aqui só estimamos.
 */
export const INTERVALO_MEDIO_SEGUNDOS = 150;

/** Quanto tempo a onda leva para sair inteira, em minutos. A primeira mensagem sai na hora. */
export function minutosParaConcluirOnda(quantidade: number): number {
  if (quantidade <= 1) return 0;
  return Math.round(((quantidade - 1) * INTERVALO_MEDIO_SEGUNDOS) / 60);
}

export function descreverDuracao(minutos: number): string {
  if (minutos <= 0) return 'na hora';
  if (minutos < 60) return `cerca de ${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m ? `cerca de ${h}h${String(m).padStart(2, '0')}` : `cerca de ${h}h`;
}
